// * Import required libraries
import { useContext, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

// * Import custom components
import SigviewTooltip from "../Common/SigviewTooltip";

// * Import utils, config & static data
import { masterTrackGaEvent } from "../../services/ga/index";

// * Import styles
import "./SidenavDrawerAdmin.scss";

// * Import contexts
import { ThemeContext } from "../../contexts/ThemeContext";
import { useHistory, useLocation } from "react-router";
import { Drawer, IconButton, makeStyles } from "@material-ui/core";
import SigviewVR from "../Common/SigviewVR";
import OpenxSVGLogo from "../../../assets/images/openxSvgNewLogo.svg";

const useStyles = makeStyles((theme) => ({
  drawer: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  paper: {
    boxShadow: "4px 0px 6px 0px rgba(0, 0, 0, 0.06)",
    border: "none",
  },
  hide: {
    width: 0,
    overflow: "hidden",
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
  },
  show: {
    width: "100%",
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
  },
  drawerOpen: {
    width: theme.spacing(30),
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
    overflowX: "hidden",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(8) + 1,
    },
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px 0px",
  },
  logo: {
    backgroundImage: `url("${OpenxSVGLogo}")`,
    backgroundSize: "90%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right",
    flex: 2,
    height: "40px",
  },
  logoTitle: {
    fontSize: "14px",
    fontWeight: "500",
    overflow: "hidden",
    lineHeight: 1.5,
    flex: 1,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0px 12px",
    // ...theme.mixins.toolbar,

    "& .MuiIconButton-root": {
      padding: "8px",
    },

    [theme.breakpoints.down("xs")]: {
      "& .MuiIconButton-root": {
        display: "none",
      },
    },
  },
}));

function SidenavDrawerAdmin(props) {
  const {
    dispatch: ReduxDispatcher,
    user,
    handleActiveTab = () => {},
    variant = "AdminGcp",
    open,
    handleDrawer,
  } = props;
  const { t } = useTranslation();

  const classes = useStyles();

  const activeTab = user.screen.activeTab;
  const { state: themeState } = useContext(ThemeContext);

  const handleTab = (e, id) => {
    handleActiveTab(e, id);
  };

  // * Define contexts
  const history = useHistory();
  const location = useLocation();
  const [isSidenavHovered, setIsSidenavHovered] = useState(false);

  //* Define Event Handlers
  const handleGroupsOpen = (e) => {
    // Google Analytics Event - Master Admin
    masterTrackGaEvent({
      category: "Admin",
      action: "GroupsOpen",
      label: "Sidenav",
    });
    handleTab(e, "groups");

    const pathname = "/admin/groups";
    history.push(pathname);
  };
  const handleUsersOpen = (e) => {
    // Google Analytics Event - Master Admin
    masterTrackGaEvent({
      category: "Admin",
      action: "UsersOpen",
      label: "Sidenav",
    });
    const pathname = "/admin/users";
    // `${location.pathname}${location.hash}${location.search}`;
    // pathname = pathname.replace("/app/#", "");
    history.push(pathname);
  };

  let activeTabHome = activeTab === "home";
  let activeTabGroups = activeTab === "groups";
  let activeTabUsers = activeTab === "users";
  let activeTabAttributes = activeTab === "attributes";

  return (
    <aside className={`sidenav-drawer-admin-container`}>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx(classes.paper, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className={classes.logoContainer}>
          <div
            className={`${classes.logo} ${!open ? classes.hide : classes.show}`}
          ></div>
          {open && <SigviewVR style={{ height: "25px" }} />}
          <div
            className={`${classes.logoTitle} ${
              !open ? classes.hide : classes.show
            }`}
          >
            Sigview
          </div>
          <div className={classes.toolbar}>
            <IconButton onClick={handleDrawer}>
              {!open ? (
                <span className="material-icons-round">menu</span>
              ) : (
                <span className="material-icons-round">close</span>
              )}
            </IconButton>
          </div>
        </div>
        <SigviewTooltip title={t("Home")} placement="right">
          <div
            className="sidenav-drawer-admin-item-wrapper"
            onMouseEnter={() => setIsSidenavHovered(true)}
            onMouseLeave={() => setIsSidenavHovered(false)}
          >
            <div
              onClick={(e) => {
                var pathname = "/admin";
                history.push(pathname);
              }}
              className={`sidenav-drawer-admin-item AdminHomeOpen-GA ${
                ((variant === "AdminGcp" && location.pathname === "/admin") ||
                  (variant === "AdminAws" && location.pathname === "/admin")) &&
                !isSidenavHovered &&
                "active"
              }`}
            >
              <div
                className={`admin-custom-home-icon ${
                  ((variant === "AdminGcp" && location.pathname === "/admin") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin")) &&
                  "sidenav-drawer-admin-active"
                }`}
              ></div>
              <span
                className={`sidenav-drawer-admin-item-title  ${
                  ((variant === "AdminGcp" && location.pathname === "/admin") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin")) &&
                  "sidenav-drawer-admin-active"
                } ${!open ? classes.hide : classes.show}`}
              >
                {t("Home")}
              </span>
            </div>
          </div>
        </SigviewTooltip>

        {variant === "AdminGcp" && (
          <SigviewTooltip title={t("Groups")} placement="right">
            <div
              className="sidenav-drawer-admin-item-wrapper"
              onMouseEnter={() => setIsSidenavHovered(true)}
              onMouseLeave={() => setIsSidenavHovered(false)}
            >
              <div
                onClick={handleGroupsOpen}
                className={`sidenav-drawer-admin-item AdminGroupsOpen-GA ${
                  variant === "AdminGcp" &&
                  location.pathname === "/admin/groups" &&
                  !isSidenavHovered &&
                  "active"
                }`}
              >
                <span
                  className={`admin-custom-group-icon material-icons-outlined ${
                    variant === "AdminGcp" &&
                    location.pathname === "/admin/groups" &&
                    "sidenav-drawer-admin-active"
                  }`}
                >
                  group
                </span>

                <span
                  className={`sidenav-drawer-admin-item-title  ${
                    variant === "AdminGcp" &&
                    location.pathname === "/admin/groups" &&
                    "sidenav-drawer-admin-active"
                  } ${!open ? classes.hide : classes.show}`}
                >
                  {t("Groups")}
                </span>
              </div>
            </div>
          </SigviewTooltip>
        )}

        <SigviewTooltip title={t("Users")} placement="right">
          <div
            className="sidenav-drawer-admin-item-wrapper"
            onMouseEnter={() => setIsSidenavHovered(true)}
            onMouseLeave={() => setIsSidenavHovered(false)}
          >
            <div
              onClick={handleUsersOpen}
              className={`sidenav-drawer-admin-item AdminUsersOpen-GA ${
                ((variant === "AdminGcp" &&
                  location.pathname === "/admin/users") ||
                  (variant === "AdminAws" &&
                    location.pathname === "/admin/users")) &&
                !isSidenavHovered &&
                "active"
              }`}
            >
              <span
                className={`admin-custom-group-icon material-icons-outlined ${
                  ((variant === "AdminGcp" &&
                    location.pathname === "/admin/users") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin/users")) &&
                  "sidenav-drawer-admin-active"
                }`}
              >
                person
              </span>

              <span
                className={`sidenav-drawer-admin-item-title ${
                  ((variant === "AdminGcp" &&
                    location.pathname === "/admin/users") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin/users")) &&
                  "sidenav-drawer-admin-active"
                } ${!open ? classes.hide : classes.show}`}
              >
                {t("Users")}
              </span>
            </div>
          </div>
        </SigviewTooltip>

        <SigviewTooltip title={t("Attributes")} placement="right">
          <div
            className="sidenav-drawer-admin-item-wrapper"
            onMouseEnter={() => setIsSidenavHovered(true)}
            onMouseLeave={() => setIsSidenavHovered(false)}
          >
            <div
              onClick={(e) => {
                var pathname = "/admin/attributes";
                history.push(pathname);
              }}
              className={`sidenav-drawer-admin-item AdminAttributeOpen-GA ${
                ((variant === "AdminGcp" &&
                  location.pathname === "/admin/attributes") ||
                  (variant === "AdminAws" &&
                    location.pathname === "/admin/attributes")) &&
                !isSidenavHovered &&
                "active"
              }`}
              id="dsBackToHome"
            >
              <span
                className={`admin-custom-group-icon material-icons-outlined ${
                  ((variant === "AdminGcp" &&
                    location.pathname === "/admin/attributes") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin/attributes")) &&
                  "sidenav-drawer-admin-active"
                }`}
              >
                trending_up
              </span>

              <span
                className={`sidenav-drawer-admin-item-title ${
                  ((variant === "AdminGcp" &&
                    location.pathname === "/admin/attributes") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin/attributes")) &&
                  "sidenav-drawer-admin-active"
                } ${!open ? classes.hide : classes.show}`}
              >
                {t("Attributes")}
              </span>
            </div>
          </div>
        </SigviewTooltip>

        {variant === "AdminGcp" && (
          <SigviewTooltip title="Reports" placement="right">
            <div
              className="sidenav-drawer-admin-item-wrapper"
              onMouseEnter={() => setIsSidenavHovered(true)}
              onMouseLeave={() => setIsSidenavHovered(false)}
            >
              <div
                onClick={(e) => {
                  var pathname = "/admin/adminReport";
                  history.push(pathname);
                }}
                className={`sidenav-drawer-admin-item AdminReportOpen-GA ${
                  ((variant === "AdminGcp" &&
                    location.pathname === "/admin/adminReport") ||
                    (variant === "AdminAws" &&
                      location.pathname === "/admin/adminReport")) &&
                  !isSidenavHovered &&
                  "active"
                }`}
              >
                <span
                  className={`admin-custom-group-icon material-icons-outlined ${
                    ((variant === "AdminGcp" &&
                      location.pathname === "/admin/adminReport") ||
                      (variant === "AdminAws" &&
                        location.pathname === "/admin/adminReport")) &&
                    "sidenav-drawer-admin-active"
                  }`}
                >
                  description
                </span>

                <span
                  className={`sidenav-drawer-admin-item-title  ${
                    ((variant === "AdminGcp" &&
                      location.pathname === "/admin/adminReport") ||
                      (variant === "AdminAws" &&
                        location.pathname === "/admin/adminReport")) &&
                    "sidenav-drawer-admin-active"
                  } ${!open ? classes.hide : classes.show}`}
                >
                  Reports
                </span>
              </div>
            </div>
          </SigviewTooltip>
        )}
      </Drawer>
    </aside>
  );
}

SidenavDrawerAdmin.propTypes = {
  user: PropTypes.object,
  match: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
});

export default connect(mapStateToProps)(SidenavDrawerAdmin);
